import {useEffect, useState} from 'react';
import {useAggsState, useFacets} from '../../contexts/AggsContext';
import api from '../api';

// Faces, Keywords, and Auto Tags load tag records after their counts arrive.
// Preserve those records during a same-search refresh, including failed refreshes.
export default function useFilterTags({auto = false, face = false, types = ['all'], organizationId}) {
    const field = auto ? 'auto_tag_names' : face ? 'face_names' : 'visible_tag_names';
    const {ready} = useFacets([field]);
    const {aggs, params, searchId, facetVersion, scopeVersion, organizationId: searchOrgId} = useAggsState();
    const aggValue = aggs[field];
    const scope = JSON.stringify([scopeVersion, organizationId, auto, face, types]);
    const [result, setResult] = useState(null);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if(!ready || !params || organizationId !== searchOrgId) return;
        const controller = new AbortController();
        let active = true;
        setResult(previous => ({scope, tags: previous?.scope === scope ? previous.tags : null, failed: false}));

        const load = async () => {
            if(auto) {
                const buckets = aggValue?.buckets || [];
                if(!buckets.length) return [];
                const {data} = await api.post('/api/auto_tags/bulk_find', {tag_names: buckets.map(bucket => bucket.key)}, {signal: controller.signal});
                if(!Array.isArray(data)) throw new Error('Tag search failed');
                return data.map(tag => ({...tag,
                    visible_assets_count: buckets.find(bucket => bucket.key === tag.name?.toLowerCase())?.doc_count || 0,
                })).sort((a, b) => b.visible_assets_count - a.visible_assets_count);
            }
            const {data} = await api.post('/api/assets/search', {...params,
                tag_aggregates_only: true, include_totals: true, aggregates_only: true,
                tag_sub_types: face ? ['person'] : types, ...(face ? {face: true} : {}),
            }, {signal: controller.signal});
            if(!Array.isArray(data)) throw new Error('Tag search failed');
            return data;
        };
        load().then(tags => {
            if(active) setResult({scope, tags, failed: false});
        }).catch(() => {
            if(active) setResult(previous => ({...previous, failed: true}));
        });
        return () => { active = false; controller.abort(); };
    }, [ready, params, searchId, facetVersion, aggValue, scope, attempt, searchOrgId]);

    const current = result?.scope === scope ? result : null;
    return {tags: current?.tags || [], loading: !current?.tags && !current?.failed,
        failed: current?.failed || false, retry: () => setAttempt(value => value + 1)};
}
